import { createContext, ReactNode, useContext, useRef } from 'react';

import { IGoogleMapsRef } from '../types';
import { DEFAULT_BOUNDS, icons } from '../constants';

interface IProps {
    children: ReactNode;
}

interface IGoogleMapsContext {
    googleMapsRef: React.MutableRefObject<IGoogleMapsRef>;
    setCurrentLocationMarker: (location: { lat: number; lng: number }) => void;
    setMapObject: (mapObject: google.maps.Map) => void;
}

const GoogleMapsContext = createContext<IGoogleMapsContext | null>(null);

export const GoogleMapsContextProvider = ({ children, ...props }: IProps) => {
    const infoWindowNode = document.createElement('div');
    const { maps } = google;

    const googleMapsRef = useRef<IGoogleMapsRef>({
        autoCompleteService: new maps.places.AutocompleteService(),
        currentLocationMarker: undefined,
        defaultBounds: new maps.LatLngBounds(
            new maps.LatLng({
                lat: DEFAULT_BOUNDS.sw.lat,
                lng: DEFAULT_BOUNDS.sw.lng,
            }),
            new maps.LatLng({
                lat: DEFAULT_BOUNDS.ne.lat,
                lng: DEFAULT_BOUNDS.ne.lng,
            })
        ),
        geocoder: new maps.Geocoder(),
        infoWindowNode,
        infoWindow: new maps.InfoWindow({
            content: infoWindowNode,
        }),
        mapObject: undefined,
        markerClusterer: undefined,
        markers: [],
    });

    const setCurrentLocationMarker = (location: {
        lat: number;
        lng: number;
    }) => {
        googleMapsRef.current.currentLocationMarker = new maps.Marker({
            position: location,
            icon: {
                url: icons.currentPosition,
                scaledSize: new maps.Size(56, 66),
            },
            map: googleMapsRef.current.mapObject,
        });
    };

    const setMapObject = (mapObject: google.maps.Map) => {
        googleMapsRef.current.mapObject = mapObject;
    };

    return (
        <GoogleMapsContext.Provider
            value={{
                googleMapsRef,
                setCurrentLocationMarker,
                setMapObject,
            }}
            {...props}
        >
            {children}
        </GoogleMapsContext.Provider>
    );
};

export function useGoogleMapsContext() {
    const context = useContext(GoogleMapsContext);

    if (context === null) {
        throw new Error('useGoogleMapsContext must be used within a provider');
    }

    return context;
}
